import { Reference } from '@approvalmax/types';
import { ActionMenu, DropdownEditor, NumberEditor, TextEditor } from '@approvalmax/ui';
import { Button, Flex } from '@approvalmax/ui/src/components';
import { intl, mathService } from '@approvalmax/utils';
import upperCase from 'lodash/upperCase';
import { constants } from 'modules/common';
import AttachmentsDropdown from 'modules/components/Attachment/AttachmentsDropdown';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import AmountDueIndicator from 'modules/request/components/xero/AmountDueIndicator';
import BeneficiaryIndicator from 'modules/request/components/xero/BeneficiaryIndicator/BeneficiaryIndicator';
import { TbEllipsisColorlessIcon } from 'modules/sprites';
import moment from 'moment';
import { RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { ColumnDefinition } from 'pages/requestList/selectors/types/ColumnDefinition';
import Skeleton from 'react-loading-skeleton';
import { notificationService } from 'services/notification';
import { calculateBillAmountDue } from 'shared/helpers';

import BankAccountDataProvider from '../../../dataProviders/BankAccountDataProvider';
import BeneficiariesDataProvider from '../../../dataProviders/BeneficiariesDataProvider/BeneficiariesDataProvider';
import { messagesConfig as messages } from './BillsInformation.messages';
import { ActionButton, FieldContainer, StyledActionMenu } from './BillsInformation.styles';
import { ColumnOptions } from './BillsInformation.types';
import BeneficiaryListItem from './components/BeneficiaryListItem/BeneficiaryListItem';

const { xeroConstants } = constants;

export const getColumnDefinitions = ({
    removeSelectableBillById,
    changeBillInvoiceItemField,
    showErrors,
    billInvoiceRequestIdsWithError,
    billInvoiceRequestIdsWithExceededAmount,
    onSelectAttachment,
    onSelectBill,
    activeAttachmentId,
    companyCurrency,
    sourceCurrencies = [],
    paymentFees = [],
    paymentPurposes = [],
    changeBeneficiary,
    changeBankAccount,
    setAddBeneficiaryPopupData,
    is2faEnabled,
    canCreateBeneficiary,
    checkBeneficiaryResultsByInvoiceId,
    editMode,
    removeLinesDisabled,
    amounts,
    isAmountsLoading,
}: ColumnOptions): ColumnDefinition<domain.AirwallexPaymentItem>[] => {
    const promisedOpenAddBeneficiary = ({ currency, id }: { currency: string; id: string }) => {
        if (is2faEnabled) {
            setAddBeneficiaryPopupData({ isOpen: true, initialBillCurrency: currency, initialBillId: id });
        } else {
            notificationService.showErrorToast(messages.no2FA);
        }

        return Promise.resolve(null);
    };

    const columns: ColumnDefinition<domain.AirwallexPaymentItem>[] = [
        {
            id: 'item',
            name: messages.itemName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.friendlyName,
            cell: (value, li) => (
                <FieldContainer width={150}>
                    <Button
                        noUppercase
                        size='small'
                        color='blue100'
                        variant='text'
                        onClick={() => onSelectBill(li.xeroBillInvoiceRequestId)}
                    >
                        {li.friendlyName}
                    </Button>
                </FieldContainer>
            ),
        },
        {
            id: 'date',
            name: messages.dateName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => moment.utc(li.date).format('ll'),
            cell: (value) => <FieldContainer width={70}>{value}</FieldContainer>,
        },
        {
            id: 'dueDate',
            name: messages.dueDateName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => moment.utc(li.dueDate).format('ll'),
            cell: (value) => <FieldContainer width={70}>{value}</FieldContainer>,
        },
        {
            id: 'amountDue',
            name: messages.amountDueName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => {
                const amountDue = calculateBillAmountDue(li, amounts, true);

                return intl.formatNumber(amountDue);
            },
            alignRight: true,
            hideTitle: true,
            cell: (value: string, li) => {
                if (isAmountsLoading) {
                    return <Skeleton />;
                }

                const billAmount = amounts?.find((amount) => amount.invoiceRequestId === li.xeroBillInvoiceRequestId);

                return (
                    <AmountDueIndicator
                        currency={li.currency}
                        amountOnApproval={billAmount?.amountOnApproval ?? li.amountOnApproval}
                        amountInProcessing={billAmount?.amountInProcessing ?? li.amountInProcessing}
                        amountAwaitingPayment={billAmount?.amountAwaitingPayment ?? li.amountAwaitingPayment}
                        amountOnReview={billAmount?.amountOnReview ?? li.amountOnReview}
                    >
                        {value}
                    </AmountDueIndicator>
                );
            },
        },
        {
            id: 'billCurrency',
            name: messages.billCurrency,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.currency,
            alignRight: true,
            hideTitle: true,
            alwaysVisible: true,
            cell: (value) => <FieldContainer width={50}>{value}</FieldContainer>,
        },
        {
            id: 'paymentAmount',
            name: messages.paymentAmount,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => String(li.amount > 0 ? li.amount : 0),
            alwaysVisible: true,
            alignRight: true,
            required: true,
            cell: (value: string, li, disabled) => {
                const maxValue = mathService.subtract(
                    li.amountDue,
                    mathService.add(
                        mathService.add(li.amountOnApproval ?? 0, li.amountInProcessing ?? 0),
                        li.amountAwaitingPayment ?? 0
                    )
                );

                return (
                    <FieldContainer width={60}>
                        <NumberEditor
                            value={Number(value)}
                            min={0}
                            align='right'
                            invalid={
                                showErrors &&
                                (!li.amount ||
                                    billInvoiceRequestIdsWithExceededAmount.includes(li.xeroBillInvoiceRequestId))
                            }
                            precision={2}
                            disabled={disabled || editMode === RequestEditMode.Approver}
                            max={maxValue > 0 ? maxValue : 0}
                            onChange={(val: number) =>
                                changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'amount', val)
                            }
                            changeOnBlur={false}
                        />
                    </FieldContainer>
                );
            },
        },
        {
            id: 'beneficiary',
            name: messages.beneficiary,
            alwaysVisible: true,
            required: true,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.beneficiary?.text || '',
            cell: (value, li) => {
                const checkBeneficiaryResult = checkBeneficiaryResultsByInvoiceId[li.xeroBillInvoiceRequestId];

                return (
                    <BeneficiaryIndicator
                        beneficiary={li.beneficiary}
                        forDropDown
                        status={checkBeneficiaryResult?.status}
                        lastPaymentProperties={checkBeneficiaryResult?.beneficiaryDiscrepancy?.lastPaymentProperties}
                    >
                        <FieldContainer width={150} title={li.beneficiary?.accountCurrency}>
                            <BeneficiariesDataProvider currencyCode={li.currency}>
                                <DropdownEditor
                                    disabled={editMode === RequestEditMode.Approver}
                                    invalid={showErrors && !value}
                                    value={li.beneficiary}
                                    onChange={(value: domain.AirwallexBeneficiary | null) => {
                                        changeBeneficiary(li.xeroBillInvoiceRequestId, value);
                                    }}
                                    buttonTitle=''
                                    placeholder={messages.beneficiaryPlaceholder}
                                    listItem={BeneficiaryListItem}
                                    canCreateItemFn={() => canCreateBeneficiary}
                                    createItemDisplayTextFn={() => messages.addBeneficiaryButton}
                                    onCreateItem={() =>
                                        promisedOpenAddBeneficiary({
                                            currency: li.currency,
                                            id: li.xeroBillInvoiceRequestId,
                                        })
                                    }
                                />
                            </BeneficiariesDataProvider>
                        </FieldContainer>
                    </BeneficiaryIndicator>
                );
            },
        },
        {
            id: 'paymentMethod',
            name: messages.paymentMethod,
            alwaysVisible: true,
            required: true,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => upperCase(li.beneficiary?.paymentMethod?.id) || '—',
        },
        {
            id: 'fee',
            name: messages.paymentFee,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.paymentFee?.id || '',
            alwaysVisible: true,
            cell: (value, li) => (
                <FieldContainer width={110}>
                    <PlainDataProvider items={paymentFees}>
                        <DropdownEditor
                            value={
                                !li.beneficiary?.paymentMethod?.id ||
                                li.beneficiary?.paymentMethod.id !== domain.AirwallexPaymentMethod.Swift
                                    ? null
                                    : li.paymentFee
                            }
                            onChange={(value: Reference) =>
                                changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'paymentFee', value)
                            }
                            disabled={
                                li.beneficiary?.paymentMethod?.id !== domain.AirwallexPaymentMethod.Swift ||
                                editMode === RequestEditMode.Approver
                            }
                            placeholder={messages.feePlaceholder}
                            invalid={
                                showErrors &&
                                !value &&
                                li.beneficiary?.paymentMethod?.id === domain.AirwallexPaymentMethod.Swift
                            }
                        />
                    </PlainDataProvider>
                </FieldContainer>
            ),
        },
        {
            id: 'sourceCurrency',
            name: messages.sourceCurrency,
            alwaysVisible: true,
            required: true,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.sourceCurrency?.id || '',
            cell: (value, li) => {
                const currencies =
                    li.currency === companyCurrency
                        ? sourceCurrencies
                        : sourceCurrencies.filter((c) => [companyCurrency, li.currency].includes(c.id));

                return (
                    <FieldContainer width={50}>
                        <PlainDataProvider items={currencies}>
                            <DropdownEditor
                                value={li.sourceCurrency}
                                onChange={(value: Reference | null) =>
                                    changeBillInvoiceItemField(
                                        li.xeroBillInvoiceRequestId,
                                        'sourceCurrency',
                                        value || li.sourceCurrency
                                    )
                                }
                                placeholder={messages.currencyPlaceholder}
                                invalid={showErrors && !value}
                                disabled={
                                    (!!li.beneficiary && li.currency !== li.beneficiary?.accountCurrency) ||
                                    editMode === RequestEditMode.Approver
                                }
                            />
                        </PlainDataProvider>
                    </FieldContainer>
                );
            },
        },
        {
            id: 'paymentReference',
            name: messages.paymentReference,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.paymentReference || '',
            alwaysVisible: true,
            required: true,
            cell: (value: string, li: any, disabled) => (
                <FieldContainer width={150}>
                    <TextEditor
                        value={value}
                        maxLength={xeroConstants.AIRWALLEX_BATCH_PAYMENT_REFERENCE}
                        disabled={disabled || editMode === RequestEditMode.Approver}
                        onChange={(val: string) =>
                            changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'paymentReference', val)
                        }
                        invalid={showErrors && !value}
                    />
                </FieldContainer>
            ),
        },
        {
            id: 'purpose',
            name: messages.paymentPurpose,
            alwaysVisible: true,
            required: true,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.paymentPurpose?.text || '',
            cell: (val, li) => (
                <FieldContainer width={150}>
                    <PlainDataProvider items={paymentPurposes}>
                        <DropdownEditor
                            value={li.paymentPurpose}
                            onChange={(value: Reference) =>
                                changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'paymentPurpose', value)
                            }
                            placeholder={messages.purposePlaceholder}
                            disabled={editMode === RequestEditMode.Approver}
                            invalid={showErrors && !val}
                        />
                    </PlainDataProvider>
                </FieldContainer>
            ),
        },
        {
            id: 'xeroBankAccount',
            name: messages.xeroBankAccount,
            alwaysVisible: true,
            required: true,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.bankAccount?.text || '',
            cell: (value, li) => (
                <FieldContainer width={150}>
                    <BankAccountDataProvider currencyCode={li.sourceCurrency?.id}>
                        <DropdownEditor
                            disabled={editMode === RequestEditMode.Approver}
                            invalid={showErrors && !value}
                            value={li.bankAccount}
                            onChange={(value: (Reference & { currencyCode: string }) | null) =>
                                changeBankAccount(
                                    li.xeroBillInvoiceRequestId,
                                    value,
                                    sourceCurrencies.find((c) => c.id === value?.currencyCode) || null
                                )
                            }
                            placeholder={messages.bankAccountPlaceholder}
                        />
                    </BankAccountDataProvider>
                </FieldContainer>
            ),
        },
        {
            id: 'attachments',
            name: messages.attachments,
            value: (li: any) => String(li.attachments.length),
            cell: (val, li: any) =>
                li.attachments.length > 0 ? (
                    <AttachmentsDropdown
                        attachments={li.attachments}
                        activeAttachmentId={activeAttachmentId}
                        onSelectAttachment={(attachmentId: string) =>
                            onSelectAttachment({
                                attachmentId,
                                xeroBatchPaymentInvoiceId: li.xeroBillInvoiceRequestId,
                                xeroBatchPaymentInvoiceName: li.friendlyName,
                            })
                        }
                    />
                ) : (
                    '—'
                ),
        },
        {
            id: 'overflowMenu',
            name: '',
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.friendlyName,
            cell: (value, li) => (
                <StyledActionMenu
                    panelFlow='center'
                    button={
                        <ActionButton>
                            <Flex alignItems='center'>
                                <TbEllipsisColorlessIcon />
                            </Flex>
                        </ActionButton>
                    }
                >
                    <ActionMenu.Item
                        disabled={removeLinesDisabled}
                        execute={() => removeSelectableBillById(li.xeroBillInvoiceRequestId)}
                        title={messages.menuItemRemove}
                    >
                        {messages.menuItemRemove}
                    </ActionMenu.Item>
                </StyledActionMenu>
            ),
        },
    ];

    return columns;
};
